import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { OTPVerification, SnackBar, GenericModal, Timer } from '@desquared/wave-components-library';

import { verifyOTP, resendOTP } from '@/api/register';
import { OTPIDStore } from '@/store/otpid';
import { maskEmail, maskMobile } from '@/utils/maskEmail';
import User from '@/store/user';
import { CompleteProfileIDStore } from '@/store/completeProfileId';
import { formatTime } from '@/utils/transformers';
import { createTokenCookie } from '@/utils/cookies';
import styles from './OTP.module.css';
import { useTranslations } from 'next-intl';
import { IsFromLoginStore } from '@/store/isFromLogin';
import { questionnaireRedirectionLink } from '@/store/questionnaire';

export default function OTP({ config, pageFrom, logo, type }) {
  const [otpValue, setOtpValue] = useState('');
  const [hasBottomError, setHasBottomError] = useState(false);
  const [bottomErrorText, setBottomErrorText] = useState('');
  const [errorText, setErrorText] = useState('');
  const router = useRouter();
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarText, setSnackBarText] = useState('');
  const [timeLeft, setTimeLeft] = useState();
  const [resendDisabled, setResendDisabled] = useState(false);
  const [totalTimeLeft, setTotalTimeLeft] = useState(config.auth.verification.registration.otp.validForSeconds);
  const [timerModal, setTimerModalOpen] = useState(false);
  const [timerOTP, setTimerOTP] = useState(false);
  const [timerOTPText, setTimerOTPText] = useState(false);

  const t = useTranslations('otp');
  const hasEmail = !config.auth.methods.some(el => el.method === 'EMAIL' && el.active);
  useEffect(() => {
    if (timeLeft === 0) {
      setResendDisabled(false);
      setSnackBarOpen(false);
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft(prevTime => prevTime - 1);
      setSnackBarText(t('retry_time', { timeLeft: formatTime(timeLeft) }));
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft, t]);

  useEffect(() => {
    if (totalTimeLeft === 0) {
      User.reset();
      router.push('/login');
      return;
    }
    if (totalTimeLeft === 120) {
      setTimerModalOpen(true);
      setTimerOTPText(t('timer_text', { totalTimeLeft: formatTime(totalTimeLeft) }));
      setTimerOTP(true);
    }

    const timer = setInterval(() => {
      setTotalTimeLeft(prevTime => prevTime - 1);
      if (totalTimeLeft < 120) {
        setTimerOTPText(t('timer_text', { totalTimeLeft: formatTime(totalTimeLeft) }));
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [totalTimeLeft]);

  const onOTPChange = e => {
    setOtpValue(e);
    setErrorText('');
    setHasBottomError(false);
    setBottomErrorText('');
  };
  const OTPInfo = OTPIDStore.getValue();
  const maskedEmail =
    OTPInfo?.email !== '' ? maskEmail(OTPInfo.email) : OTPInfo?.mobilePhone ? maskMobile(OTPInfo?.mobilePhone) : '';

  const submit = async () => {
    const { data, error } = await verifyOTP({ requestId: OTPInfo.requestId, otp: otpValue }, router.locale);
    if (error) {
      if (error.code === 'OTP0003') {
        setErrorText(t('errors.timer'));
        return;
      } else if (error.code === 'OTP0002') {
        setErrorText(t('errors.invalid'));
        return;
      }
      setHasBottomError(true);
      setBottomErrorText(t('errors.generic'));
    } else {
      OTPIDStore.update({ ...OTPInfo, otp: otpValue });
      if (data) {
        User.update({
          completeProfileRequestId: data?.completeProfileRequestId,
          loyaltyCards: data.user.loyaltyCards,
          email: data.user.email,
          stat: data.status,
          _id: data.user._id,
          firstName: data.user?.firstName ?? '',
          lastName: data.user?.lastName ?? '',
        });
        if (data?.completeProfileRequestId) {
          CompleteProfileIDStore.update(data.completeProfileRequestId);
        }
      }

      if (pageFrom === 'password-reset') {
        router.push('/create-new-password');
      } else if (data.status === 'SUCCESS') {
        createTokenCookie(data.authToken.token);
        User.update({
          ...data.user,
          authToken: data.authToken.token,
        });
        IsFromLoginStore.update(true);
        if (questionnaireRedirectionLink.getValue() && questionnaireRedirectionLink.getValue().length > 0) {
          const questionnaireLink = questionnaireRedirectionLink.getValue();
          const [pathname, queryString] = questionnaireLink.split('?');

          // If there's a query string, parse it
          const params = new URLSearchParams(queryString);
          const customerId = params.get('customerId');
          questionnaireRedirectionLink.update('');
          router.push({
            pathname: pathname,
            query: { customerId: customerId },
          });
        } else {
          router.push({
            pathname: '/',
          });
        }
      } else {
        router.push('/account-details');
      }
    }
  };

  const resend = async () => {
    setResendDisabled(true);
    const { data, error } = await resendOTP(
      { requestId: OTPInfo.requestId, verificationCase: 'VERIFY' },
      router.locale
    );
    if (error) {
      setHasBottomError(true);
      setBottomErrorText(t('errors.generic'));
    } else {
      OTPIDStore.update({ requestId: data.otpInfo.requestId, email: OTPInfo.email, mobilePhone: OTPInfo.mobilePhone });
      setTimeLeft(config.auth.verification.forgotPassword.otp.resendAllowedInSeconds);
      setSnackBarText(
        t('retry_snackbar', {
          resendSeconds: formatTime(config.auth.verification.forgotPassword.otp.resendAllowedInSeconds),
        })
      );
      setSnackBarOpen(true);
    }
  };

  return (
    <>
      <div className={type === 0 ? styles.maxWidthContainer : styles.container}>
        <OTPVerification
          logo={logo}
          logoRedirect={router.locale === router.defaultLocale ? '/' : `/${router.locale}/`}
          titleText={!hasEmail ? t('title') : t('title_mobile')}
          description={!hasEmail ? t('description') : t('description_mobile')}
          email={OTPInfo.requestId ? maskedEmail : ''}
          errorText={errorText}
          continueText={t('continue')}
          resend={t('resend')}
          resendText={t('resend_description')}
          hasBottomError={hasBottomError}
          bottomErrorText={bottomErrorText}
          hasBottomSuccess={false}
          bottomSuccessText={t('title')}
          onChangeOTP={onOTPChange}
          otpInputsValues={otpValue}
          onContinueClick={submit}
          otpPattern={config.auth.verification.registration.otp.pattern}
          onClickResend={resend}
          isResendDisabled={resendDisabled}
          hasAlert={timerOTP}
          alertText={timerOTPText}
          classOption={type === 1 || type === 2 ? styles.paddingContainer : styles.noPaddingContainer}
        />
      </div>
      <div style={{ width: snackBarOpen ? '100%' : 'auto' }}>
        <SnackBar
          open={snackBarOpen}
          setOpen={() => setSnackBarOpen(false)}
          text={snackBarText}
          hasCloseOption={true}
          className={styles.snackBarContainer}
          position={'center'}
        />
      </div>
      {timerModal && (
        <GenericModal
          icon={Timer}
          title={t('timer_modal.title')}
          description={t('timer_modal.description')}
          cancelWording=""
          onPrimaryClick={() => setTimerModalOpen(false)}
          primaryButtonWording={t('timer_modal.close')}
        />
      )}
    </>
  );
}
